import { SITE_NAME, SITE_ORIGIN } from "../lib/site";

export type PageSeo = {
  title: string;
  description: string;
  path: string;
  /** When true, adds noindex (e.g. admin). */
  private?: boolean;
};

const defaultDescription =
  "SpecOps Engineers LLC builds and hardens software and device systems: architecture, production code, tests, and release automation.";

export const defaultSeo: PageSeo = {
  title: `${SITE_NAME} | Software and systems engineering consultancy`,
  description: defaultDescription,
  path: "/",
};

export const pageSeo: Record<string, PageSeo> = {
  "/": {
    title: `${SITE_NAME} | Software and systems engineering consultancy`,
    description:
      "Architecture, production code, tests, and release work for apps, cloud services, and connected devices.",
    path: "/",
  },
  "/services": {
    title: `Services | ${SITE_NAME}`,
    description:
      "Architecture, production engineering, release gates, and engineering leadership for product teams.",
    path: "/services",
  },
  "/experience": {
    title: `Selected work | ${SITE_NAME}`,
    description:
      "Past work: private OTA for a device fleet, a Metal rendering engine for Apple platforms, and a React Native marketplace on AWS.",
    path: "/experience",
  },
  "/contact": {
    title: `Contact | ${SITE_NAME}`,
    description:
      "Contact SpecOps Engineers about architecture, engineering, or release work. We reply within two business days.",
    path: "/contact",
  },
  "/privacy": {
    title: `Privacy policy | ${SITE_NAME}`,
    description:
      "Privacy policy for SpecOps Engineers LLC, including GDPR and California CCPA/CPRA rights for contact form data.",
    path: "/privacy",
  },
  "/admin": {
    title: `Admin sign-in | ${SITE_NAME}`,
    description: "Administrative sign-in for SpecOps Engineers site settings.",
    path: "/admin",
    private: true,
  },
};

export function seoForPath(pathname: string): PageSeo {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  return pageSeo[normalized] ?? defaultSeo;
}

export function absoluteUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_ORIGIN}${p === "/" ? "/" : p}`;
}

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  description: defaultDescription,
  areaServed: {
    "@type": "Country",
    name: "United States",
  },
  serviceType: [
    "Software engineering consulting",
    "Software architecture",
    "Mobile application development",
    "Continuous integration and release engineering",
    "Quality and test engineering",
    "DevOps consulting",
    "Embedded systems engineering",
  ],
};
